import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTenant } from "../lib/tenant-context";

interface TakebackItem {
  id: string;
  name: string;
  image?: string;
  condition: string;
  creditValue: number;
}

interface TakebackEvent {
  id: string;
  type: string;
  timestamp: string;
  data?: Record<string, unknown>;
}

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  productContext?: Record<string, unknown>;
}

interface TakebackDetail {
  id: string;
  customerName: string;
  customerEmail?: string;
  customerId?: string;
  storeId: string;
  storeName: string;
  kioskId: string;
  status: string;
  sessionType: string;
  items: TakebackItem[];
  itemCount: number;
  creditValue: number;
  converted: boolean;
  conversionValue: number;
  events: TakebackEvent[];
  messages: ChatMessage[];
  startedAt: string;
  endedAt?: string;
  createdAt: string;
}

export const Route = createFileRoute("/pin_/takeback_/$id")({
  component: TakebackDetailPage,
});

function TakebackDetailPage() {
  const { id } = Route.useParams();
  const { currentTenant } = useTenant();
  const [takeback, setTakeback] = useState<TakebackDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTakeback();
  }, [id, currentTenant.id]);

  const loadTakeback = async () => {
    try {
      const response = await fetch(`/api/v1/pin/takebacks/${id}`, {
        headers: {
          "X-Tenant-ID": currentTenant.id,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setTakeback(data);
      } else if (response.status === 404) {
        // Try demo data for demo IDs
        if (
          id.startsWith("tb-") ||
          id.startsWith("rec-") ||
          id.startsWith("pin_")
        ) {
          setTakeback(getDemoTakeback(id, currentTenant.id));
        } else {
          setError("Takeback not found");
        }
      } else {
        setError("Failed to load takeback");
      }
    } catch (err) {
      console.error("Failed to load takeback:", err);
      // Fallback to demo data
      if (
        id.startsWith("tb-") ||
        id.startsWith("rec-") ||
        id.startsWith("pin_")
      ) {
        setTakeback(getDemoTakeback(id, currentTenant.id));
      } else {
        setError("Failed to load takeback");
      }
    } finally {
      setLoading(false);
    }
  };

  const getDemoTakeback = (tbId: string, tenantId: string): TakebackDetail => {
    // Arc'teryx demo takebacks
    const arcteryxTakebacks: Record<string, TakebackDetail> = {
      "tb-001": {
        id: "tb-001",
        customerName: "Customer #10482",
        customerId: "ARC-10482",
        storeId: "arcteryx-vancouver",
        storeName: "Arc'teryx Vancouver Flagship",
        kioskId: "pin-van-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-1",
            name: "Beta AR Jacket",
            image: "/images/products/arcteryx/beta-ar-jacket.jpeg",
            condition: "excellent",
            creditValue: 280,
          },
          {
            id: "item-2",
            name: "Atom LT Hoody",
            image: "/images/products/arcteryx/atom-lt-hoody.jpeg",
            condition: "good",
            creditValue: 120,
          },
        ],
        itemCount: 2,
        creditValue: 400,
        converted: true,
        conversionValue: 1100,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 1800000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 1750000).toISOString(),
            data: { product: "Beta AR Jacket" },
          },
          {
            id: "evt-3",
            type: "condition_assessed",
            timestamp: new Date(Date.now() - 1720000).toISOString(),
            data: { condition: "excellent", score: 9 },
          },
          {
            id: "evt-4",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 1700000).toISOString(),
            data: { product: "Atom LT Hoody" },
          },
          {
            id: "evt-5",
            type: "condition_assessed",
            timestamp: new Date(Date.now() - 1680000).toISOString(),
            data: { condition: "good", score: 7 },
          },
          {
            id: "evt-6",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 1650000).toISOString(),
            data: { credit: 400 },
          },
          {
            id: "evt-7",
            type: "offer_accepted",
            timestamp: new Date(Date.now() - 1620000).toISOString(),
          },
          {
            id: "evt-8",
            type: "purchase_made",
            timestamp: new Date(Date.now() - 1600000).toISOString(),
            data: { amount: 1100, product: "Alpha SV Jacket" },
          },
        ],
        messages: [
          {
            id: "msg-1",
            role: "assistant",
            content:
              "Welcome to Arc'teryx ReBird! I see you have a Beta AR Jacket to trade in. This is one of our most popular shells!",
            timestamp: new Date(Date.now() - 1750000).toISOString(),
          },
          {
            id: "msg-2",
            role: "user",
            content:
              "Yes, I've had it for 3 years but want to upgrade to the Alpha SV",
            timestamp: new Date(Date.now() - 1740000).toISOString(),
          },
          {
            id: "msg-3",
            role: "assistant",
            content:
              "Great choice! The Alpha SV offers even more durability and weather protection. Your Beta AR is in excellent condition - I can offer $280 in trade credit for it.",
            timestamp: new Date(Date.now() - 1730000).toISOString(),
          },
        ],
        startedAt: new Date(Date.now() - 1800000).toISOString(),
        endedAt: new Date(Date.now() - 1600000).toISOString(),
        createdAt: new Date(Date.now() - 1800000).toISOString(),
      },
      "tb-002": {
        id: "tb-002",
        customerName: "Customer #10495",
        customerId: "ARC-10495",
        storeId: "arcteryx-whistler",
        storeName: "Arc'teryx Whistler",
        kioskId: "pin-whi-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-3",
            name: "Cerium LT Jacket",
            image: "/images/products/arcteryx/cerium-lt-jacket.jpeg",
            condition: "excellent",
            creditValue: 175,
          },
          {
            id: "item-4",
            name: "Gamma LT Pants",
            image: "/images/products/arcteryx/gamma-lt-pants.jpeg",
            condition: "good",
            creditValue: 85,
          },
          {
            id: "item-5",
            name: "Delta LT Zip Neck",
            image: "/images/products/arcteryx/delta-lt-zip-neck.jpeg",
            condition: "excellent",
            creditValue: 55,
          },
        ],
        itemCount: 3,
        creditValue: 315,
        converted: true,
        conversionValue: 625,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 3600000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 3550000).toISOString(),
          },
          {
            id: "evt-3",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 3500000).toISOString(),
          },
          {
            id: "evt-4",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 3450000).toISOString(),
          },
          {
            id: "evt-5",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 3420000).toISOString(),
          },
          {
            id: "evt-6",
            type: "offer_accepted",
            timestamp: new Date(Date.now() - 3400000).toISOString(),
          },
        ],
        messages: [],
        startedAt: new Date(Date.now() - 3600000).toISOString(),
        endedAt: new Date(Date.now() - 3400000).toISOString(),
        createdAt: new Date(Date.now() - 3600000).toISOString(),
      },
      "tb-003": {
        id: "tb-003",
        customerName: "Customer #10503",
        customerId: "ARC-10503",
        storeId: "arcteryx-seattle",
        storeName: "Arc'teryx Seattle",
        kioskId: "pin-sea-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-6",
            name: "Proton FL Hoody",
            image: "/images/products/arcteryx/proton-fl-hoody.jpeg",
            condition: "fair",
            creditValue: 95,
          },
        ],
        itemCount: 1,
        creditValue: 95,
        converted: false,
        conversionValue: 0,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 7200000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 7150000).toISOString(),
          },
          {
            id: "evt-3",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 7100000).toISOString(),
          },
          {
            id: "evt-4",
            type: "offer_declined",
            timestamp: new Date(Date.now() - 7000000).toISOString(),
          },
        ],
        messages: [],
        startedAt: new Date(Date.now() - 7200000).toISOString(),
        endedAt: new Date(Date.now() - 7000000).toISOString(),
        createdAt: new Date(Date.now() - 7200000).toISOString(),
      },
    };

    // Lululemon demo takebacks
    const lululemonTakebacks: Record<string, TakebackDetail> = {
      "tb-001": {
        id: "tb-001",
        customerName: "Customer #70201",
        customerId: "LLL-70201",
        storeId: "lululemon-short-hills",
        storeName: "Short Hills Mall",
        kioskId: "pin-nj-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-1",
            name: 'Align High-Rise Pant 25"',
            image: "/images/lululemon/align-pant.jpg",
            condition: "excellent",
            creditValue: 25,
          },
          {
            id: "item-2",
            name: "Scuba Oversized Half-Zip",
            image: "/images/lululemon/scuba-hoodie.jpg",
            condition: "good",
            creditValue: 20,
          },
        ],
        itemCount: 2,
        creditValue: 45,
        converted: true,
        conversionValue: 118,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 1800000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 1750000).toISOString(),
          },
          {
            id: "evt-3",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 1700000).toISOString(),
          },
          {
            id: "evt-4",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 1650000).toISOString(),
          },
          {
            id: "evt-5",
            type: "offer_claimed",
            timestamp: new Date(Date.now() - 1600000).toISOString(),
          },
        ],
        messages: [],
        startedAt: new Date(Date.now() - 1800000).toISOString(),
        endedAt: new Date(Date.now() - 1600000).toISOString(),
        createdAt: new Date(Date.now() - 1800000).toISOString(),
      },
      "tb-002": {
        id: "tb-002",
        customerName: "Customer #70218",
        customerId: "LLL-70218",
        storeId: "lululemon-5th-ave",
        storeName: "Fifth Avenue Flagship",
        kioskId: "pin-nyc-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-3",
            name: 'Wunder Train High-Rise Tight 28"',
            image: "/images/lululemon/wunder-train.jpg",
            condition: "excellent",
            creditValue: 30,
          },
          {
            id: "item-4",
            name: "Energy Bra High Support",
            condition: "good",
            creditValue: 22,
          },
          {
            id: "item-5",
            name: "Define Jacket",
            condition: "excellent",
            creditValue: 20,
          },
        ],
        itemCount: 3,
        creditValue: 72,
        converted: true,
        conversionValue: 196,
        events: [],
        messages: [],
        startedAt: new Date(Date.now() - 3600000).toISOString(),
        endedAt: new Date(Date.now() - 3400000).toISOString(),
        createdAt: new Date(Date.now() - 3600000).toISOString(),
      },
      "tb-003": {
        id: "tb-003",
        customerName: "Customer #70229",
        customerId: "LLL-70229",
        storeId: "lululemon-short-hills",
        storeName: "Short Hills Mall",
        kioskId: "pin-nj-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-6",
            name: "Fast and Free Running Hat",
            condition: "fair",
            creditValue: 35,
          },
        ],
        itemCount: 1,
        creditValue: 35,
        converted: false,
        conversionValue: 0,
        events: [],
        messages: [],
        startedAt: new Date(Date.now() - 7200000).toISOString(),
        endedAt: new Date(Date.now() - 7000000).toISOString(),
        createdAt: new Date(Date.now() - 7200000).toISOString(),
      },
    };

    // Nike demo takebacks
    const nikeTakebacks: Record<string, TakebackDetail> = {
      "tb-001": {
        id: "tb-001",
        customerName: "Customer #50412",
        customerId: "NKE-50412",
        storeId: "nike-house-of-innovation",
        storeName: "Nike House of Innovation NYC",
        kioskId: "pin-nyc-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-1",
            name: "Air Force 1 '07",
            image:
              "https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/b7d9211c-26e7-431a-ac24-b0540fb3c00f/air-force-1-07-shoes.png",
            condition: "excellent",
            creditValue: 49,
          },
          {
            id: "item-2",
            name: "Air Jordan 1 Retro High OG",
            image:
              "https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/4ca942e0-c08a-4eaf-a41b-1782ddea9c3b/air-jordan-1-retro-high-og-shoes.png",
            condition: "good",
            creditValue: 54,
          },
        ],
        itemCount: 2,
        creditValue: 103,
        converted: true,
        conversionValue: 180,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 1800000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 1750000).toISOString(),
            data: { product: "Air Force 1 '07" },
          },
          {
            id: "evt-3",
            type: "condition_assessed",
            timestamp: new Date(Date.now() - 1720000).toISOString(),
            data: { condition: "excellent", score: 9 },
          },
          {
            id: "evt-4",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 1700000).toISOString(),
            data: { product: "Air Jordan 1 Retro High OG" },
          },
          {
            id: "evt-5",
            type: "condition_assessed",
            timestamp: new Date(Date.now() - 1680000).toISOString(),
            data: { condition: "good", score: 7 },
          },
          {
            id: "evt-6",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 1650000).toISOString(),
            data: { credit: 103 },
          },
          {
            id: "evt-7",
            type: "offer_accepted",
            timestamp: new Date(Date.now() - 1620000).toISOString(),
          },
          {
            id: "evt-8",
            type: "purchase_made",
            timestamp: new Date(Date.now() - 1600000).toISOString(),
            data: { amount: 180, product: "Air Jordan 1 Retro High OG" },
          },
        ],
        messages: [
          {
            id: "msg-1",
            role: "assistant",
            content:
              "Welcome to Nike Refurbished! I see you have a classic pair of Air Force 1s to trade in. These are iconic!",
            timestamp: new Date(Date.now() - 1750000).toISOString(),
          },
          {
            id: "msg-2",
            role: "user",
            content:
              "Yeah, I've had them for a while but want to get the new Jordan 1s",
            timestamp: new Date(Date.now() - 1740000).toISOString(),
          },
          {
            id: "msg-3",
            role: "assistant",
            content:
              "Great choice! Your Air Force 1s are in excellent condition - I can offer $49 in trade credit. Combined with your Jordans, you'll have $103 toward your new pair!",
            timestamp: new Date(Date.now() - 1730000).toISOString(),
          },
        ],
        startedAt: new Date(Date.now() - 1800000).toISOString(),
        endedAt: new Date(Date.now() - 1600000).toISOString(),
        createdAt: new Date(Date.now() - 1800000).toISOString(),
      },
      "tb-002": {
        id: "tb-002",
        customerName: "Customer #50428",
        customerId: "NKE-50428",
        storeId: "nike-soho",
        storeName: "Nike SoHo",
        kioskId: "pin-soho-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-3",
            name: "Pegasus 41",
            image:
              "https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/c0e93f87-b5c1-42c0-bda4-f6c8ccb3af3f/pegasus-41-road-running-shoes.png",
            condition: "excellent",
            creditValue: 63,
          },
          {
            id: "item-4",
            name: "Dunk Low Retro",
            image:
              "https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/b1bcbca4-e853-4df7-b329-5be3c61ee057/dunk-low-retro-shoes.png",
            condition: "good",
            creditValue: 33,
          },
          {
            id: "item-5",
            name: "Air Max 90",
            condition: "excellent",
            creditValue: 45,
          },
        ],
        itemCount: 3,
        creditValue: 141,
        converted: true,
        conversionValue: 280,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 3600000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 3550000).toISOString(),
          },
          {
            id: "evt-3",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 3500000).toISOString(),
          },
          {
            id: "evt-4",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 3450000).toISOString(),
          },
          {
            id: "evt-5",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 3420000).toISOString(),
          },
          {
            id: "evt-6",
            type: "offer_accepted",
            timestamp: new Date(Date.now() - 3400000).toISOString(),
          },
        ],
        messages: [],
        startedAt: new Date(Date.now() - 3600000).toISOString(),
        endedAt: new Date(Date.now() - 3400000).toISOString(),
        createdAt: new Date(Date.now() - 3600000).toISOString(),
      },
      "tb-003": {
        id: "tb-003",
        customerName: "Customer #50441",
        customerId: "NKE-50441",
        storeId: "nike-house-of-innovation",
        storeName: "Nike House of Innovation NYC",
        kioskId: "pin-nyc-002",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-6",
            name: "Tech Fleece Hoodie",
            condition: "fair",
            creditValue: 39,
          },
        ],
        itemCount: 1,
        creditValue: 39,
        converted: false,
        conversionValue: 0,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 7200000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 7150000).toISOString(),
          },
          {
            id: "evt-3",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 7100000).toISOString(),
          },
          {
            id: "evt-4",
            type: "offer_declined",
            timestamp: new Date(Date.now() - 7000000).toISOString(),
          },
        ],
        messages: [],
        startedAt: new Date(Date.now() - 7200000).toISOString(),
        endedAt: new Date(Date.now() - 7000000).toISOString(),
        createdAt: new Date(Date.now() - 7200000).toISOString(),
      },
    };

    // Adidas demo takebacks
    const adidasTakebacks: Record<string, TakebackDetail> = {
      "tb-001": {
        id: "tb-001",
        customerName: "Customer #40291",
        customerId: "ADI-40291",
        storeId: "adidas-5th-avenue",
        storeName: "adidas 5th Avenue NYC",
        kioskId: "pin-adidas-nyc-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-1",
            name: "Ultraboost 22",
            image:
              "https://assets.adidas.com/images/h_840,f_auto,q_auto,fl_lossy,c_fill,g_auto/fbaf991a78bc4896a3e9ad7800abcec6_9366/Ultraboost_22_Shoes_Black_GZ0127_01_standard.jpg",
            condition: "excellent",
            creditValue: 52,
          },
          {
            id: "item-2",
            name: "Stan Smith",
            image:
              "https://assets.adidas.com/images/h_840,f_auto,q_auto,fl_lossy,c_fill,g_auto/68ae7ea7849b43eca70aac1e00f5146d_9366/Stan_Smith_Shoes_White_FX5502_01_standard.jpg",
            condition: "good",
            creditValue: 20,
          },
        ],
        itemCount: 2,
        creditValue: 72,
        converted: true,
        conversionValue: 180,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 1800000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 1750000).toISOString(),
            data: { product: "Ultraboost 22" },
          },
          {
            id: "evt-3",
            type: "condition_assessed",
            timestamp: new Date(Date.now() - 1720000).toISOString(),
            data: { condition: "excellent", score: 9 },
          },
          {
            id: "evt-4",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 1700000).toISOString(),
            data: { product: "Stan Smith" },
          },
          {
            id: "evt-5",
            type: "condition_assessed",
            timestamp: new Date(Date.now() - 1680000).toISOString(),
            data: { condition: "good", score: 7 },
          },
          {
            id: "evt-6",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 1650000).toISOString(),
            data: { credit: 72 },
          },
          {
            id: "evt-7",
            type: "offer_accepted",
            timestamp: new Date(Date.now() - 1620000).toISOString(),
          },
          {
            id: "evt-8",
            type: "purchase_made",
            timestamp: new Date(Date.now() - 1600000).toISOString(),
            data: { amount: 180, product: "Ultraboost Light" },
          },
        ],
        messages: [
          {
            id: "msg-1",
            role: "assistant",
            content:
              "Welcome to adidas Infinite Play! I see you have an Ultraboost 22 to trade in. Great running shoe!",
            timestamp: new Date(Date.now() - 1750000).toISOString(),
          },
          {
            id: "msg-2",
            role: "user",
            content: "Yes, looking to upgrade to the new Ultraboost Light",
            timestamp: new Date(Date.now() - 1740000).toISOString(),
          },
          {
            id: "msg-3",
            role: "assistant",
            content:
              "Excellent choice! Your Ultraboost 22 is in excellent condition - I can offer $52 in trade credit for it.",
            timestamp: new Date(Date.now() - 1730000).toISOString(),
          },
        ],
        startedAt: new Date(Date.now() - 1800000).toISOString(),
        endedAt: new Date(Date.now() - 1600000).toISOString(),
        createdAt: new Date(Date.now() - 1800000).toISOString(),
      },
      "tb-002": {
        id: "tb-002",
        customerName: "Customer #40318",
        customerId: "ADI-40318",
        storeId: "adidas-soho",
        storeName: "adidas Originals SoHo",
        kioskId: "pin-adidas-nyc-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-3",
            name: "Forum Low",
            image: "/assets/images/products/adidas/adi2000-shoes.jpg",
            condition: "excellent",
            creditValue: 35,
          },
          {
            id: "item-4",
            name: "Samba OG",
            image:
              "https://assets.adidas.com/images/h_840,f_auto,q_auto,fl_lossy,c_fill,g_auto/7ed0855435194229a525aad6009a0497_9366/Samba_OG_Shoes_White_B75806_01_standard.jpg",
            condition: "good",
            creditValue: 30,
          },
          {
            id: "item-5",
            name: "Gazelle",
            image: "/assets/images/products/adidas/handball-spezial-shoes.jpg",
            condition: "excellent",
            creditValue: 28,
          },
        ],
        itemCount: 3,
        creditValue: 93,
        converted: true,
        conversionValue: 220,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 3600000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 3550000).toISOString(),
          },
          {
            id: "evt-3",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 3500000).toISOString(),
          },
          {
            id: "evt-4",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 3450000).toISOString(),
          },
          {
            id: "evt-5",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 3420000).toISOString(),
          },
          {
            id: "evt-6",
            type: "offer_accepted",
            timestamp: new Date(Date.now() - 3400000).toISOString(),
          },
        ],
        messages: [],
        startedAt: new Date(Date.now() - 3600000).toISOString(),
        endedAt: new Date(Date.now() - 3400000).toISOString(),
        createdAt: new Date(Date.now() - 3600000).toISOString(),
      },
      "tb-003": {
        id: "tb-003",
        customerName: "Customer #40325",
        customerId: "ADI-40325",
        storeId: "adidas-5th-avenue",
        storeName: "adidas 5th Avenue NYC",
        kioskId: "pin-adidas-nyc-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-6",
            name: "NMD_R1",
            image:
              "/assets/images/products/adidas/Rapidmove_Pro_Training_Shoes_Orange_JS4268_HM1.jpg",
            condition: "fair",
            creditValue: 28,
          },
        ],
        itemCount: 1,
        creditValue: 28,
        converted: false,
        conversionValue: 0,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 7200000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 7150000).toISOString(),
          },
          {
            id: "evt-3",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 7100000).toISOString(),
          },
          {
            id: "evt-4",
            type: "offer_declined",
            timestamp: new Date(Date.now() - 7000000).toISOString(),
          },
        ],
        messages: [],
        startedAt: new Date(Date.now() - 7200000).toISOString(),
        endedAt: new Date(Date.now() - 7000000).toISOString(),
        createdAt: new Date(Date.now() - 7200000).toISOString(),
      },
      "tb-004": {
        id: "tb-004",
        customerName: "Customer #40337",
        customerId: "ADI-40337",
        storeId: "adidas-chi",
        storeName: "adidas Chicago",
        kioskId: "pin-adidas-chi-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-7",
            name: "Superstar",
            image: "/assets/images/products/adidas/anthony-edwards-2-shoes.jpg",
            condition: "good",
            creditValue: 32,
          },
          {
            id: "item-8",
            name: "Adicolor Classics Trefoil Hoodie",
            image:
              "/assets/images/products/adidas/adidas-z.n.e.-puffer-climawarm-down-jacket.jpg",
            condition: "excellent",
            creditValue: 32,
          },
        ],
        itemCount: 2,
        creditValue: 64,
        converted: true,
        conversionValue: 160,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 10800000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 10750000).toISOString(),
            data: { product: "Superstar" },
          },
          {
            id: "evt-3",
            type: "condition_assessed",
            timestamp: new Date(Date.now() - 10720000).toISOString(),
            data: { condition: "good", score: 7 },
          },
          {
            id: "evt-4",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 10700000).toISOString(),
            data: { product: "Adicolor Classics Trefoil Hoodie" },
          },
          {
            id: "evt-5",
            type: "condition_assessed",
            timestamp: new Date(Date.now() - 10680000).toISOString(),
            data: { condition: "excellent", score: 9 },
          },
          {
            id: "evt-6",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 10650000).toISOString(),
            data: { credit: 64 },
          },
          {
            id: "evt-7",
            type: "offer_accepted",
            timestamp: new Date(Date.now() - 10620000).toISOString(),
          },
          {
            id: "evt-8",
            type: "purchase_made",
            timestamp: new Date(Date.now() - 10600000).toISOString(),
            data: { amount: 160, product: "Adizero Adios Pro 3" },
          },
        ],
        messages: [
          {
            id: "msg-1",
            role: "assistant",
            content:
              "Welcome to adidas Infinite Play! Let me assess your items for trade-in credit.",
            timestamp: new Date(Date.now() - 10750000).toISOString(),
          },
          {
            id: "msg-2",
            role: "user",
            content:
              "I'd like to trade these in and put the credit toward new running shoes",
            timestamp: new Date(Date.now() - 10740000).toISOString(),
          },
          {
            id: "msg-3",
            role: "assistant",
            content:
              "Great choice! Your Superstar is in good condition ($32 credit) and the Trefoil Hoodie is excellent ($32 credit). That's $64 total — perfect toward Adizero running shoes.",
            timestamp: new Date(Date.now() - 10730000).toISOString(),
          },
        ],
        startedAt: new Date(Date.now() - 10800000).toISOString(),
        endedAt: new Date(Date.now() - 10600000).toISOString(),
        createdAt: new Date(Date.now() - 10800000).toISOString(),
      },
      "tb-005": {
        id: "tb-005",
        customerName: "Guest",
        storeId: "adidas-soho",
        storeName: "adidas Originals SoHo",
        kioskId: "pin-adidas-nyc-001",
        status: "declined",
        sessionType: "takeback",
        items: [
          {
            id: "item-9",
            name: "Terrex AX4",
            image:
              "/assets/images/products/adidas/terrex-anylander-hiking-shoes.jpg",
            condition: "fair",
            creditValue: 18,
          },
        ],
        itemCount: 1,
        creditValue: 18,
        converted: false,
        conversionValue: 0,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 14400000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 14350000).toISOString(),
            data: { product: "Terrex AX4" },
          },
          {
            id: "evt-3",
            type: "condition_assessed",
            timestamp: new Date(Date.now() - 14320000).toISOString(),
            data: { condition: "fair", score: 4 },
          },
          {
            id: "evt-4",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 14300000).toISOString(),
            data: { credit: 18 },
          },
          {
            id: "evt-5",
            type: "offer_declined",
            timestamp: new Date(Date.now() - 14200000).toISOString(),
          },
        ],
        messages: [],
        startedAt: new Date(Date.now() - 14400000).toISOString(),
        endedAt: new Date(Date.now() - 14200000).toISOString(),
        createdAt: new Date(Date.now() - 14400000).toISOString(),
      },
      "tb-006": {
        id: "tb-006",
        customerName: "Customer #40352",
        customerId: "ADI-40352",
        storeId: "adidas-5th-avenue",
        storeName: "adidas 5th Avenue NYC",
        kioskId: "pin-adidas-nyc-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-10",
            name: "Handball Spezial",
            image: "/assets/images/products/adidas/handball-spezial-shoes.jpg",
            condition: "excellent",
            creditValue: 42,
          },
          {
            id: "item-11",
            name: "Samba Classic",
            image: "/assets/images/products/adidas/handball-spezial-shoes.jpg",
            condition: "excellent",
            creditValue: 38,
          },
          {
            id: "item-12",
            name: "Adicolor Essentials Tee",
            image:
              "/assets/images/products/adidas/originals-workwear-canvas-jacket.jpg",
            condition: "good",
            creditValue: 15,
          },
          {
            id: "item-13",
            name: "Tiro 23 League Pants",
            image:
              "/assets/images/products/adidas/originals-canvas-cargo-workpant.jpg",
            condition: "good",
            creditValue: 22,
          },
        ],
        itemCount: 4,
        creditValue: 117,
        converted: true,
        conversionValue: 320,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 21600000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 21550000).toISOString(),
            data: { product: "Handball Spezial" },
          },
          {
            id: "evt-3",
            type: "condition_assessed",
            timestamp: new Date(Date.now() - 21520000).toISOString(),
            data: { condition: "excellent", score: 9 },
          },
          {
            id: "evt-4",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 21500000).toISOString(),
            data: { product: "Samba Classic" },
          },
          {
            id: "evt-5",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 21450000).toISOString(),
            data: { product: "Adicolor Essentials Tee" },
          },
          {
            id: "evt-6",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 21400000).toISOString(),
            data: { product: "Tiro 23 League Pants" },
          },
          {
            id: "evt-7",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 21350000).toISOString(),
            data: { credit: 117 },
          },
          {
            id: "evt-8",
            type: "offer_accepted",
            timestamp: new Date(Date.now() - 21320000).toISOString(),
          },
          {
            id: "evt-9",
            type: "purchase_made",
            timestamp: new Date(Date.now() - 21300000).toISOString(),
            data: {
              amount: 320,
              product: "Ultraboost 5 + Tiro 23 Training Set",
            },
          },
        ],
        messages: [
          {
            id: "msg-1",
            role: "assistant",
            content:
              "Welcome back! I see you have several items to trade in today. Let me scan each one.",
            timestamp: new Date(Date.now() - 21550000).toISOString(),
          },
          {
            id: "msg-2",
            role: "user",
            content:
              "Yes, clearing out my closet. Want to get some new training gear.",
            timestamp: new Date(Date.now() - 21540000).toISOString(),
          },
          {
            id: "msg-3",
            role: "assistant",
            content:
              "Great haul! 4 items assessed — $117 in total credit. Your Handball Spezial and Samba Classic are both in excellent condition. Would you like to browse new arrivals?",
            timestamp: new Date(Date.now() - 21380000).toISOString(),
          },
        ],
        startedAt: new Date(Date.now() - 21600000).toISOString(),
        endedAt: new Date(Date.now() - 21300000).toISOString(),
        createdAt: new Date(Date.now() - 21600000).toISOString(),
      },
      "tb-007": {
        id: "tb-007",
        customerName: "Customer #40371",
        customerId: "ADI-40371",
        storeId: "adidas-chi",
        storeName: "adidas Chicago",
        kioskId: "pin-adidas-chi-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-14",
            name: "Ozweego",
            image: "/assets/images/products/adidas/adi2000-shoes.jpg",
            condition: "good",
            creditValue: 35,
          },
          {
            id: "item-15",
            name: "Firebird Track Top",
            image:
              "/assets/images/products/adidas/street-neuclassics-track-top.jpg",
            condition: "excellent",
            creditValue: 50,
          },
        ],
        itemCount: 2,
        creditValue: 85,
        converted: true,
        conversionValue: 195,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 28800000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 28750000).toISOString(),
            data: { product: "Ozweego" },
          },
          {
            id: "evt-3",
            type: "condition_assessed",
            timestamp: new Date(Date.now() - 28720000).toISOString(),
            data: { condition: "good", score: 7 },
          },
          {
            id: "evt-4",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 28700000).toISOString(),
            data: { product: "Firebird Track Top" },
          },
          {
            id: "evt-5",
            type: "condition_assessed",
            timestamp: new Date(Date.now() - 28680000).toISOString(),
            data: { condition: "excellent", score: 9 },
          },
          {
            id: "evt-6",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 28650000).toISOString(),
            data: { credit: 85 },
          },
          {
            id: "evt-7",
            type: "offer_accepted",
            timestamp: new Date(Date.now() - 28620000).toISOString(),
          },
          {
            id: "evt-8",
            type: "purchase_made",
            timestamp: new Date(Date.now() - 28600000).toISOString(),
            data: { amount: 195, product: "Ultraboost 22 Gore-Tex" },
          },
        ],
        messages: [],
        startedAt: new Date(Date.now() - 28800000).toISOString(),
        endedAt: new Date(Date.now() - 28600000).toISOString(),
        createdAt: new Date(Date.now() - 28800000).toISOString(),
      },
    };

    // Demo Brand demo takebacks
    const demobrandTakebacks: Record<string, TakebackDetail> = {
      "tb-001": {
        id: "tb-001",
        customerName: "Customer #60103",
        customerId: "DMO-60103",
        storeId: "demobrand-downtown",
        storeName: "Demo Brand Downtown",
        kioskId: "pin-dt-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-1",
            name: "Velocity Pro Running Shoes",
            image:
              "/assets/images/products/demobrand/velocity-pro-running-white.png",
            condition: "excellent",
            creditValue: 75,
          },
          {
            id: "item-2",
            name: "FlexMotion Quarter-Zip",
            image:
              "/assets/images/products/demobrand/flexmotion-quarter-zip-gray.png",
            condition: "good",
            creditValue: 40,
          },
        ],
        itemCount: 2,
        creditValue: 115,
        converted: true,
        conversionValue: 220,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 1800000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 1750000).toISOString(),
            data: { product: "Velocity Pro Running Shoes" },
          },
          {
            id: "evt-3",
            type: "condition_assessed",
            timestamp: new Date(Date.now() - 1720000).toISOString(),
            data: { condition: "excellent", score: 9 },
          },
          {
            id: "evt-4",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 1700000).toISOString(),
            data: { product: "FlexMotion Quarter-Zip" },
          },
          {
            id: "evt-5",
            type: "condition_assessed",
            timestamp: new Date(Date.now() - 1680000).toISOString(),
            data: { condition: "good", score: 7 },
          },
          {
            id: "evt-6",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 1650000).toISOString(),
            data: { credit: 115 },
          },
          {
            id: "evt-7",
            type: "offer_accepted",
            timestamp: new Date(Date.now() - 1620000).toISOString(),
          },
          {
            id: "evt-8",
            type: "purchase_made",
            timestamp: new Date(Date.now() - 1600000).toISOString(),
            data: { amount: 220, product: "ThermoCore Training Jacket" },
          },
        ],
        messages: [
          {
            id: "msg-1",
            role: "assistant",
            content:
              "Welcome to Demo Brand Trade-In! I see you have Velocity Pro Running Shoes to trade in. Great choice for runners!",
            timestamp: new Date(Date.now() - 1750000).toISOString(),
          },
          {
            id: "msg-2",
            role: "user",
            content:
              "Yes, I've had them for about a year but want to try the new ThermoCore jacket",
            timestamp: new Date(Date.now() - 1740000).toISOString(),
          },
          {
            id: "msg-3",
            role: "assistant",
            content:
              "Excellent choice! Your running shoes are in excellent condition - I can offer $75 in trade credit. Combined with your quarter-zip, you'll have $115 toward your new jacket!",
            timestamp: new Date(Date.now() - 1730000).toISOString(),
          },
        ],
        startedAt: new Date(Date.now() - 1800000).toISOString(),
        endedAt: new Date(Date.now() - 1600000).toISOString(),
        createdAt: new Date(Date.now() - 1800000).toISOString(),
      },
      "tb-002": {
        id: "tb-002",
        customerName: "Customer #60118",
        customerId: "DMO-60118",
        storeId: "demobrand-mall",
        storeName: "Demo Brand Mall Location",
        kioskId: "pin-mall-001",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-3",
            name: "Women's FlexFit Leggings",
            image:
              "/assets/images/products/demobrand/flexfit-leggings-black.png",
            condition: "excellent",
            creditValue: 35,
          },
          {
            id: "item-4",
            name: "Women's BreatheTech Sports Bra",
            image:
              "/assets/images/products/demobrand/breathetech-sports-bra.png",
            condition: "good",
            creditValue: 25,
          },
          {
            id: "item-5",
            name: "Women's Yoga Flow Tank",
            image: "/assets/images/products/demobrand/yoga-flow-tank-coral.png",
            condition: "excellent",
            creditValue: 20,
          },
        ],
        itemCount: 3,
        creditValue: 80,
        converted: true,
        conversionValue: 165,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 3600000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 3550000).toISOString(),
          },
          {
            id: "evt-3",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 3500000).toISOString(),
          },
          {
            id: "evt-4",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 3450000).toISOString(),
          },
          {
            id: "evt-5",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 3420000).toISOString(),
          },
          {
            id: "evt-6",
            type: "offer_accepted",
            timestamp: new Date(Date.now() - 3400000).toISOString(),
          },
        ],
        messages: [],
        startedAt: new Date(Date.now() - 3600000).toISOString(),
        endedAt: new Date(Date.now() - 3400000).toISOString(),
        createdAt: new Date(Date.now() - 3600000).toISOString(),
      },
      "tb-003": {
        id: "tb-003",
        customerName: "Customer #60125",
        customerId: "DMO-60125",
        storeId: "demobrand-downtown",
        storeName: "Demo Brand Downtown",
        kioskId: "pin-dt-002",
        status: "completed",
        sessionType: "takeback",
        items: [
          {
            id: "item-6",
            name: "Men's Training Shorts",
            image: "/assets/images/products/demobrand/mens-training-shorts.png",
            condition: "fair",
            creditValue: 20,
          },
        ],
        itemCount: 1,
        creditValue: 20,
        converted: false,
        conversionValue: 0,
        events: [
          {
            id: "evt-1",
            type: "session_started",
            timestamp: new Date(Date.now() - 7200000).toISOString(),
          },
          {
            id: "evt-2",
            type: "product_scanned",
            timestamp: new Date(Date.now() - 7150000).toISOString(),
          },
          {
            id: "evt-3",
            type: "offer_shown",
            timestamp: new Date(Date.now() - 7100000).toISOString(),
          },
          {
            id: "evt-4",
            type: "offer_declined",
            timestamp: new Date(Date.now() - 7000000).toISOString(),
          },
        ],
        messages: [],
        startedAt: new Date(Date.now() - 7200000).toISOString(),
        endedAt: new Date(Date.now() - 7000000).toISOString(),
        createdAt: new Date(Date.now() - 7200000).toISOString(),
      },
    };

    // Select demo data based on tenant
    const demoTakebacks =
      tenantId === "arcteryx"
        ? arcteryxTakebacks
        : tenantId === "nike"
          ? nikeTakebacks
          : tenantId === "adidas"
            ? adidasTakebacks
            : tenantId === "demobrand"
              ? demobrandTakebacks
              : lululemonTakebacks;

    return (
      demoTakebacks[tbId] || {
        id: tbId,
        customerName: "Guest",
        storeId: "unknown",
        storeName: "Unknown Store",
        kioskId: "unknown",
        status: "pending",
        sessionType: "takeback",
        items: [],
        itemCount: 0,
        creditValue: 0,
        converted: false,
        conversionValue: 0,
        events: [],
        messages: [],
        startedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      }
    );
  };

  if (loading) {
    return (
      <div className="max-w-4xl p-12 text-center">
        <div
          className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto mb-4"
          style={{ borderColor: "#00FBA9" }}
        />
        <p style={{ color: "#A9A9A9" }}>Loading takeback details...</p>
      </div>
    );
  }

  if (error || !takeback) {
    return (
      <div className="max-w-4xl p-12 text-center">
        <p className="text-lg mb-4" style={{ color: "#FF4C00" }}>
          {error || "Takeback not found"}
        </p>
        <Link to="/pin" className="text-sm" style={{ color: "#00FBA9" }}>
          Back to Physical Intelligence
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/pin"
          className="text-sm flex items-center gap-1 mb-4"
          style={{ color: "#A9A9A9" }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path
              d="M15 18L9 12L15 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Back to Physical Intelligence
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1
              className="text-2xl font-light mb-1"
              style={{ color: "#171717" }}
            >
              Trade-In — {takeback.customerName}
            </h1>
            <p className="text-sm" style={{ color: "#A9A9A9" }}>
              {takeback.storeName} • {formatDateTime(takeback.startedAt)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={takeback.status} />
            {takeback.converted && (
              <span
                className="px-3 py-1 rounded-full text-xs font-medium"
                style={{
                  backgroundColor: "rgba(0,251,169,0.15)",
                  color: "#00FBA9",
                }}
              >
                Converted
              </span>
            )}
          </div>
        </div>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <SummaryCard
          label="Items Traded"
          value={takeback.itemCount.toString()}
        />
        <SummaryCard
          label="Credit Issued"
          value={`$${takeback.creditValue}`}
          highlight
        />
        <SummaryCard
          label="Purchase Made"
          value={takeback.converted ? `$${takeback.conversionValue}` : "—"}
        />
        <SummaryCard
          label="Net Value"
          value={
            takeback.converted
              ? `+$${takeback.conversionValue - takeback.creditValue}`
              : "—"
          }
          highlight={takeback.converted}
        />
      </div>
      {/* Main Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Items List */}
        <div className="col-span-2">
          <div
            className="bg-white border"
            style={{ borderColor: "rgba(169,169,169,0.2)" }}
          >
            <div
              className="px-6 py-4 border-b"
              style={{ borderColor: "rgba(169,169,169,0.1)" }}
            >
              <h2 className="font-medium" style={{ color: "#171717" }}>
                Items Traded In
              </h2>
            </div>
            <div
              className="divide-y"
              style={{ borderColor: "rgba(169,169,169,0.1)" }}
            >
              {takeback.items.length > 0 ? (
                takeback.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-4 px-6 py-4"
                  >
                    <div
                      className="w-16 h-16 overflow-hidden"
                      style={{ backgroundColor: "#FAFAFA" }}
                    >
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div
                          className="w-full h-full flex items-center justify-center text-xs"
                          style={{ color: "#A9A9A9" }}
                        >
                          No image
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <p
                        className="font-medium text-sm"
                        style={{ color: "#171717" }}
                      >
                        {item.name}
                      </p>
                      <p
                        className="text-xs capitalize"
                        style={{ color: "#A9A9A9" }}
                      >
                        Condition: {item.condition}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium" style={{ color: "#00FBA9" }}>
                        ${item.creditValue}
                      </p>
                      <p className="text-xs" style={{ color: "#A9A9A9" }}>
                        credit
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-6 py-8 text-center">
                  <p style={{ color: "#A9A9A9" }}>No items recorded</p>
                </div>
              )}
            </div>
          </div>

          {/* Timeline */}
          {takeback.events.length > 0 && (
            <div
              className="bg-white border mt-6"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              <div
                className="px-6 py-4 border-b"
                style={{ borderColor: "rgba(169,169,169,0.1)" }}
              >
                <h2 className="font-medium" style={{ color: "#171717" }}>
                  Timeline
                </h2>
              </div>
              <div className="px-6 py-4">
                <div className="space-y-4">
                  {takeback.events.map((event, idx) => (
                    <div key={event.id} className="flex items-start gap-4">
                      <div className="flex flex-col items-center">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: "#00FBA9" }}
                        />
                        {idx < takeback.events.length - 1 && (
                          <div
                            className="w-0.5 h-8 mt-1"
                            style={{ backgroundColor: "rgba(169,169,169,0.2)" }}
                          />
                        )}
                      </div>
                      <div>
                        <p
                          className="text-sm font-medium capitalize"
                          style={{ color: "#171717" }}
                        >
                          {event.type.replace(/_/g, " ")}
                        </p>
                        <p className="text-xs" style={{ color: "#A9A9A9" }}>
                          {formatTime(event.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Customer Info & Chat */}
        <div>
          {/* Customer Card */}
          <div
            className="bg-white border mb-6"
            style={{ borderColor: "rgba(169,169,169,0.2)" }}
          >
            <div
              className="px-6 py-4 border-b"
              style={{ borderColor: "rgba(169,169,169,0.1)" }}
            >
              <h2 className="font-medium" style={{ color: "#171717" }}>
                Customer
              </h2>
            </div>
            <div className="px-6 py-4">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "#FAFAFA" }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#A9A9A9"
                    strokeWidth="1.5"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium" style={{ color: "#171717" }}>
                    {takeback.customerName}
                  </p>
                  {takeback.customerId && (
                    <p className="text-xs" style={{ color: "#A9A9A9" }}>
                      {takeback.customerId}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Session Info */}
          <div
            className="bg-white border"
            style={{ borderColor: "rgba(169,169,169,0.2)" }}
          >
            <div
              className="px-6 py-4 border-b"
              style={{ borderColor: "rgba(169,169,169,0.1)" }}
            >
              <h2 className="font-medium" style={{ color: "#171717" }}>
                Session Details
              </h2>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-xs" style={{ color: "#A9A9A9" }}>
                  Store
                </span>
                <span className="text-sm" style={{ color: "#171717" }}>
                  {takeback.storeName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs" style={{ color: "#A9A9A9" }}>
                  Kiosk
                </span>
                <span className="text-sm" style={{ color: "#171717" }}>
                  {takeback.kioskId}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs" style={{ color: "#A9A9A9" }}>
                  Started
                </span>
                <span className="text-sm" style={{ color: "#171717" }}>
                  {formatTime(takeback.startedAt)}
                </span>
              </div>
              {takeback.endedAt && (
                <div className="flex justify-between">
                  <span className="text-xs" style={{ color: "#A9A9A9" }}>
                    Ended
                  </span>
                  <span className="text-sm" style={{ color: "#171717" }}>
                    {formatTime(takeback.endedAt)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-xs" style={{ color: "#A9A9A9" }}>
                  Duration
                </span>
                <span className="text-sm" style={{ color: "#171717" }}>
                  {takeback.endedAt
                    ? formatDuration(takeback.startedAt, takeback.endedAt)
                    : "In progress"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className="bg-white border p-4"
      style={{ borderColor: "rgba(169,169,169,0.2)" }}
    >
      <p
        className="text-xs uppercase tracking-wider mb-2"
        style={{ color: "#A9A9A9" }}
      >
        {label}
      </p>
      <p
        className="text-2xl font-light"
        style={{ color: highlight ? "#00FBA9" : "#171717" }}
      >
        {value}
      </p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; color: string }> = {
    completed: { bg: "rgba(0,251,169,0.15)", color: "#00FBA9" },
    pending: { bg: "rgba(169,169,169,0.15)", color: "#A9A9A9" },
    declined: { bg: "rgba(255,76,0,0.15)", color: "#FF4C00" },
    active: { bg: "rgba(57,65,235,0.15)", color: "#3941EB" },
  };
  const style = styles[status] || styles.pending;

  return (
    <span
      className="px-3 py-1 rounded-full text-xs font-medium capitalize"
      style={{ backgroundColor: style.bg, color: style.color }}
    >
      {status}
    </span>
  );
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDuration(startStr: string, endStr: string): string {
  const start = new Date(startStr);
  const end = new Date(endStr);
  const diffMs = end.getTime() - start.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 60) {
    return `${diffMins} min`;
  }
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  return `${hours}h ${mins}m`;
}
